import { useState, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowRight } from "lucide-react";

const NAV_LINKS = [
  { label: "Space", to: "/space" },
  { label: "Nexus", to: "/nexus" },
  { label: "Cerebrum", to: "/cerebrum" },
  { label: "Cloud", to: "/cloud" },
  { label: "Pricing", to: "/space/rogue/pricing" },
  { label: "About", to: "/about" },
];

export default function Navbar({ onTrialClick }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const toggle = useCallback(() => setMobileOpen((v) => !v), []);

  return (
    <nav
      data-testid="navbar"
      className="fixed top-0 left-0 right-0 z-50 bg-[#0a0b0e]/90 border-b border-white/[0.06]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 flex items-center justify-between h-16 md:h-20">
        {/* Wordmark */}
        <Link
          to="/"
          data-testid="navbar-logo"
          className="hover:opacity-80 transition-opacity duration-250"
        >
          <img
            src="/logos/geminii/white-blue.png"
            alt="Geminii"
            className="h-7 md:h-8 w-auto"
          />
        </Link>

        {/* Desktop links */}
        <div className="hidden md:flex items-center gap-8">
          {NAV_LINKS.map((link) => {
            if (link.external) {
              return (
                <a
                  key={link.label}
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`nav-link-${link.label.toLowerCase()}`}
                  className="text-sm font-medium transition-colors duration-250 text-geminii-muted hover:text-white"
                >
                  {link.label}
                </a>
              );
            }
            return (
              <Link
                key={link.label}
                to={link.to}
                data-testid={`nav-link-${link.label.toLowerCase()}`}
                className={`text-sm font-medium transition-colors duration-250 ${
                  (link.to === "/space"
                    ? location.pathname === "/space" || (location.pathname.startsWith("/space/") && !location.pathname.startsWith("/space/rogue/pricing"))
                    : location.pathname.startsWith(link.to))
                    ? "text-rogue-cyan"
                    : "text-geminii-muted hover:text-white"
                }`}
              >
                {link.label}
              </Link>
            );
          })}
          <button
            onClick={onTrialClick}
            data-testid="navbar-cta"
            className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-rogue-cyan text-geminii-deep text-sm font-semibold hover:shadow-[0_0_20px_rgba(31,182,193,0.4)] transition-all duration-250"
          >
            Start free trial
            <ArrowRight size={14} />
          </button>
        </div>

        {/* Mobile toggle */}
        <button
          onClick={toggle}
          data-testid="navbar-mobile-toggle"
          className="md:hidden text-white p-2"
        >
          {mobileOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="md:hidden overflow-hidden bg-[#0a0b0e]/95 backdrop-blur-xl border-b border-white/[0.06]"
            data-testid="navbar-mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {NAV_LINKS.map((link) => {
                if (link.external) {
                  return (
                    <a
                      key={link.label}
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={toggle}
                      className="text-base font-medium py-2 text-geminii-muted hover:text-white"
                    >
                      {link.label}
                    </a>
                  );
                }
                return (
                  <Link
                    key={link.label}
                    to={link.to}
                    onClick={toggle}
                    className={`text-base font-medium py-2 ${
                      (link.to === "/space"
                        ? location.pathname === "/space" || (location.pathname.startsWith("/space/") && !location.pathname.startsWith("/space/rogue/pricing"))
                        : location.pathname.startsWith(link.to))
                        ? "text-rogue-cyan"
                        : "text-geminii-muted hover:text-white"
                    }`}
                  >
                    {link.label}
                  </Link>
                );
              })}
              <button
                onClick={() => {
                  toggle();
                  onTrialClick();
                }}
                data-testid="navbar-mobile-cta"
                className="mt-2 flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-rogue-cyan text-geminii-deep text-sm font-semibold"
              >
                Start free trial
                <ArrowRight size={14} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}
